import { FC, useState } from "react";
import ReportStatusSelector from "./ReportStatusSelector";
import ReportDateFilter from "./ReportDateFilter";
import ReportLocationFilter from "./ReportLocationFilter";
import ReportEventConcernTypeFilter from "./ReportEventConcernTypeFilter";
import { useReportFilter } from "../../hooks/useReportFilter";
import ReportReporterDetailsFilter from "./ReportReporterDetailsFilter";
import ReportAbuserInfoFilter from "./ReportAbuserInfoFilter";
import FilterCriteriaSelect from "./FilterCriteriaSelect";
import { FetchedReportData } from "../../../types";
import { isDefaultFilterCriteria } from "../../../utils/filterUtils";
import devLogger from "../../../utils/devLogger";
import { reportStatusOptions } from "../../SubmitTip/resources";
import ReportSupportServicesFilter from "./ReportSupportServicesFilter";
import LoadingAnimation from "../../common/LoadingAnimation";
import filterSvg from "../../../images/filter-outline.svg";
import exportSvg from "../../../images/export-variant.svg";
import restartSvg from "../../../images/restart.svg";
import { FaX } from "react-icons/fa6";

const ModeToggleButton: FC<{
  name: ActiveMode;
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
  activeClass: string;
  defaultClass: string;
}> = ({ name, active, icon, label, onClick, activeClass, defaultClass }) => (
  <button
    name={name}
    onClick={onClick}
    className={`flex items-center gap-2 font-extrabold text-base leading-5 px-6 py-[11px] rounded-[24px] ${
      active ? activeClass : defaultClass
    }`}
  >
    <img
      src={icon}
      alt=""
      className={`w-5 h-5 ${
        name === "filter"
          ? !active && "filter invert"
          : active && "filter invert"
      }`}
    />
    {label}
  </button>
);

const ActionButton: FC<ActionButtonProps> = ({
  onClick,
  label,
  classes,
  icon,
  disabled = false,
}) => (
  <button className={classes} onClick={onClick} disabled={disabled}>
    {icon && <img src={icon} alt={`${label} icon`} className="w-5 h-5" />}
    {label}
  </button>
);

const FilterExportSection: FC<FilterExportSectionProps> = ({
  activeMode = "filter",
  setActiveMode,
  setFilteredReports,
}) => {
  const {
    activeFilters,
    filterCriteria,
    filtering,
    setFilterCriteria,
    handleFilterChange,
    handleFilter,
  } = useReportFilter();

  const [isVisible, setIsVisible] = useState(false);

  const filterReports = async () => {
    const res = await handleFilter();
    devLogger.log("🚀 ~ filterReports ~ res:", res);
    setFilteredReports(res);
  };

  const resetClearBtnClasses =
    "flex items-center text-white font-[600] text-base leading-5 px-4 py-1.5 rounded-[24px] bg-gray-7 gap-1 disabled:bg-gray-3 disabled:text-gray-4";

  return (
    <>
      {!isVisible && (
        <div className="pt-5 h-md:pt-10 lg:hidden">
          <button
            className="flex justify-center items-center bg-gray-7 hover:bg-gray-6 text-white rounded-full h-[54px] w-[54px] lg:hidden z-50 shadow-lg"
            onClick={() => setIsVisible(true)}
          >
            <img src={filterSvg} alt="Filter" className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Sliding Filter Panel */}
      <div
        className={`flex flex-col fixed lg:static top-[75px] sm:top-[87px] lg:top-0 left-0 h-[calc(100%-75px)] sm:h-[calc(100%-87px)] lg:h-full z-40 lg:z-auto transition-transform transform ${
          isVisible ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 w-[420px] px-5 lg:px-0 lg:w-[389px] border-r shadow-lg lg:border-none lg:shadow-none bg-white lg:bg-transparent`}
      >
        {/* Close button for mobile view */}
        <div className="lg:hidden text-gray-7 pt-5 pr-5 text-right">
          <button
            className="text-gray-7 rounded-full p-2 cursor-pointer"
            onClick={() => setIsVisible(!isVisible)}
          >
            <FaX />
          </button>
        </div>

        <div className="flex gap-4 pt-5 h-md:pt-10">
          <ModeToggleButton
            name="filter"
            active={activeMode === "filter"}
            icon={filterSvg}
            label="Filter"
            onClick={() => setActiveMode("filter")}
            activeClass="text-white bg-gray-7 border-2 border-gray-7"
            defaultClass="text-gray-7 bg-transparent border-2 border-gray-7"
          />

          <ModeToggleButton
            name="export"
            active={activeMode === "export"}
            icon={exportSvg}
            label="Export to CSV"
            onClick={() => setActiveMode("export")}
            activeClass="text-white bg-success-dark border-success-dark"
            defaultClass="text-gray-7 bg-transparent border-2 border-gray-7"
          />
        </div>

        <div className="mb-4">
          <div className="w-full flex justify-between mt-[60px] mb-6">
            <ActionButton
              // onClick={() => setFilterCriteria(defaultFilterCriteria)}
              onClick={() => window.location.reload()}
              icon={restartSvg}
              label="Reset"
              classes={resetClearBtnClasses}
            />
            <ActionButton
              onClick={() => setFilteredReports(undefined)}
              disabled={!filterReports || filterReports.length > 0}
              label="Clear Result"
              classes={resetClearBtnClasses}
            />
          </div>
          <FilterCriteriaSelect handleFilterChange={handleFilterChange} />
        </div>

        <div className="flex flex-col gap-3 flex-grow overflow-y-auto pr-1.5">
          {activeFilters.eventConcern && (
            <ReportEventConcernTypeFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.supportServices && (
            <ReportSupportServicesFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.location && (
            <ReportLocationFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.date && (
            <ReportDateFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.reporterDetails && (
            <ReportReporterDetailsFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.abuserInfo && (
            <ReportAbuserInfoFilter
              filterCriteria={filterCriteria}
              setFilterCriteria={setFilterCriteria}
            />
          )}

          {activeFilters.status && (
            <ReportStatusSelector
              options={[
                { label: "All", value: undefined },
                ...reportStatusOptions,
              ]}
              reportStatus={filterCriteria.reportStatus}
              setReportStatus={(reportStatus) =>
                setFilterCriteria((prev) => ({
                  ...prev,
                  reportStatus: reportStatus,
                }))
              }
            />
          )}
        </div>

        <div className="flex justify-center w-full py-3">
          <button
            className="h-[52px] self-center w-auto sm:w-[335px] font-extrabold text-lg leading-[22px] text-center bg-black disabled:bg-gray-4 text-white rounded-3xl py-[15px] px-[109.5px]"
            onClick={filterReports}
            disabled={isDefaultFilterCriteria(filterCriteria)}
          >
            {filtering ? <LoadingAnimation /> : "Show results"}
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterExportSection;

type ActiveMode = "filter" | "export";

interface FilterExportSectionProps {
  activeMode?: ActiveMode;
  setActiveMode: (activeMode: ActiveMode) => void;
  setFilteredReports: (
    filteredReports: FetchedReportData[] | undefined
  ) => void;
}

interface ActionButtonProps {
  onClick: () => void;
  label: string;
  classes: string;
  icon?: string; // Optional icon
  disabled?: boolean; // Optional disabled state
}
